<template>
    <CustomDialog
        v-model="isVisible"
        :defaultTitle="isEdit ? $t('webhooks.form.title_edit') : $t('webhooks.form.title_add')"
        width="640px"
    >
        <template #content>
            <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                <CustomInput
                    :label="$t('webhooks.form.url')"
                    v-model="form.url"
                    placeholder="https://exemple.fr/webhooks/playpro"
                    :required="true"
                    :errorText="errors.url"
                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                    customLabelClass="poppinsLight ppv2-text-black"
                />

                <CustomInput
                    :label="$t('webhooks.form.description')"
                    v-model="form.description"
                    :placeholder="$t('webhooks.form.description_placeholder')"
                    :errorText="errors.description"
                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                    customLabelClass="poppinsLight ppv2-text-black"
                />

                <div>
                    <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-1">
                        <label class="poppinsLight ppv2-text-black" :style="`font-size: ${normalizeSize(15, true)}`">
                            {{ $t('webhooks.form.events') }}
                        </label>
                        <button type="button" class="ppv2-text-xs ppv2-underline ppv2-text-[#7D2DBD]" @click="toggleAll">
                            {{ allSelected ? $t('api.form.scopes.none') : $t('api.form.scopes.all') }}
                        </button>
                    </div>
                    <div class="ppv2-border ppv2-border-gray-200 ppv2-rounded-lg ppv2-p-3 ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <label
                            v-for="event in events"
                            :key="event.name"
                            class="ppv2-flex ppv2-items-start ppv2-gap-2 ppv2-cursor-pointer"
                        >
                            <input type="checkbox" :value="event.name" v-model="form.events" class="ppv2-mt-1" />
                            <span>
                                <code class="ppv2-text-xs ppv2-bg-gray-100 ppv2-px-1 ppv2-rounded">{{ event.name }}</code>
                                <span class="ppv2-block ppv2-text-xs ppv2-text-gray-500">{{ event.description }}</span>
                            </span>
                        </label>
                    </div>
                    <p v-if="errors.events" class="ppv2-text-xs ppv2-text-red-600 ppv2-mt-1">{{ errors.events }}</p>
                </div>

                <BaseSwitch
                    :label="$t('webhooks.form.active')"
                    v-model="form.active"
                    :boxed="true"
                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                    customLabelClass="poppinsLight ppv2-text-black"
                />
            </div>
        </template>

        <template #actions>
            <BaseButton variant="outline" @click="isVisible = false" :customHeight="48" customClass="!ppv2-min-w-[160px]">
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton
                variant="primary"
                :loading="isSaving"
                @click="save"
                :customHeight="48"
                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
            >
                {{ isEdit ? $t('common.validate') : $t('common.add') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import axios from 'axios'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'

const emptyForm = () => ({ url: '', description: '', events: [], active: true })

export default {
    name: 'WebhookForm',
    components: { CustomInput, BaseButton, BaseSwitch, CustomDialog },
    props: {
        value: { type: Boolean, default: false },
        webhook: { type: Object, default: null },
        events: { type: Array, default: () => [] },
        token: { type: String, default: '' },
    },
    data() {
        return { form: emptyForm(), errors: {}, isSaving: false }
    },
    computed: {
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        isEdit() {
            return !!this.webhook
        },
        allSelected() {
            return this.events.length > 0 && this.form.events.length === this.events.length
        },
    },
    watch: {
        value(open) {
            if (!open) return
            this.errors = {}
            this.form = this.webhook
                ? {
                      url: this.webhook.url,
                      description: this.webhook.description || '',
                      events: [...this.webhook.events],
                      active: this.webhook.active,
                  }
                : emptyForm()
        },
    },
    methods: {
        normalizeSize,
        toggleAll() {
            this.form.events = this.allSelected ? [] : this.events.map((e) => e.name)
        },
        async save() {
            this.isSaving = true
            this.errors = {}
            const config = { headers: { Authorization: `Bearer ${this.token}` } }
            try {
                const { data } = this.isEdit
                    ? await axios.patch(`/api/v3/webhooks/${this.webhook.id}`, this.form, config)
                    : await axios.post('/api/v3/webhooks', this.form, config)
                this.$toast.success(this.$t('webhooks.save_success'))
                this.$emit('saved', data.data)
                this.isVisible = false
            } catch (error) {
                const errors = error.response?.data?.errors
                if (errors) {
                    this.errors = {
                        url: errors.url?.[0],
                        description: errors.description?.[0],
                        events: (errors.events ||
                            Object.keys(errors)
                                .filter((k) => k.startsWith('events.'))
                                .map((k) => errors[k][0]))[0],
                    }
                } else {
                    this.$toast.error(error.response?.data?.message || this.$t('common.save_error'))
                }
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>
